import type { AddressFieldKey, ValidationError } from "@/services/address-validation/address-validation.types";

/**
 * HOP's `AddressValidationError.message` is documented as already
 * human-readable ("Human-readable error message describing the problem"
 * per the generated `@amzn/hardware-order-processing-api-client` model),
 * and `element` names the affected field (e.g. "zip", "state") -- neither
 * needs client-side translation. This mapper's only job is attributing
 * `message` to the caller's own form field via `element`.
 */
const FIELD_BY_VALIDATION_ELEMENT: Record<string, AddressFieldKey> = {
  address1: "addressLine1",
  line1: "addressLine1",
  address2: "addressLine2",
  line2: "addressLine2",
  address3: "addressLine3",
  line3: "addressLine3",
  city: "city",
  state: "state",
  province: "state",
  postalcode: "postalCode",
  zip: "postalCode",
  zipcode: "postalCode",
  country: "country",
  county: "county",
  voice: "phoneNumber",
  phonenumber: "phoneNumber",
};

export interface AddressValidationErrors<TFormField extends string> {
  fieldErrors: Partial<Record<TFormField, string>>;
  unattributedMessages: string[];
}

/**
 * Converts HOP validation errors into form field errors, using a
 * caller-supplied dictionary to translate the domain-generic `AddressFieldKey`
 * into that particular form's own field name. This lets any checkout-style
 * form reuse the same element-to-field mapping even if its fields are named
 * differently from `CheckoutFormState`.
 *
 * A validation error whose `element` doesn't resolve to a known field is
 * collected into `unattributedMessages` instead of being dropped, so
 * callers can still surface it as a standalone submission error.
 */
export const toAddressFormValidationErrors = <TFormField extends string>(
  validationErrors: ValidationError[],
  formFieldByAddressField: Record<AddressFieldKey, TFormField>
): AddressValidationErrors<TFormField> => {
  const fieldErrors: Partial<Record<TFormField, string>> = {};
  const unattributedMessages: string[] = [];
  for (const validationError of validationErrors) {
    if (!validationError.message) {
      continue;
    }
    const addressFieldKey = FIELD_BY_VALIDATION_ELEMENT[validationError.element?.trim().toLowerCase() ?? ""];
    const formField = addressFieldKey ? formFieldByAddressField[addressFieldKey] : undefined;
    if (formField) {
      fieldErrors[formField] = validationError.message;
    } else {
      unattributedMessages.push(validationError.message);
    }
  }
  return { fieldErrors, unattributedMessages };
};
